import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
  CreatePodcastRequestDtoCefrLevel,
} from "@packages/api/madlenAPI.schemas";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import { Button, Text } from "../../../../../core";
import { Input } from "@core/input";
import CourseSelector from "../course-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { useAppSelector } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";
import { CEFRSelector } from "@core/cefr-selector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

interface YoutubeStepProps {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}

const YoutubeStep: React.FC<
  YoutubeStepProps & {
    assignmentData: CreateHomeworkAssignmentRequestBody & {
      cefrLevel?: CreatePodcastRequestDtoCefrLevel;
    };
    onChange: (
      data: Partial<
        CreateHomeworkAssignmentRequestBody & {
          cefrLevel?: CreatePodcastRequestDtoCefrLevel;
        }
      >,
    ) => void;
  }
> = ({ assignmentData, onChange }) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState<string>(assignmentData.prompt || "");
  const [youtubeLink, setYoutubeLink] = useState<string>(
    assignmentData.youtubeLink || "",
  );
  const [selectedGrade, setSelectedGrade] = useState<number>(
    assignmentData.grade || 8,
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const [selectedCefrLevel, setSelectedCefrLevel] =
    useState<CreatePodcastRequestDtoCefrLevel>(
      (assignmentData.cefrLevel as CreatePodcastRequestDtoCefrLevel) ||
        CreatePodcastRequestDtoCefrLevel.A1,
    );
  // İngilizce default kurs, course selector gizli
  useEffect(() => {
    setTopic("");
    onChange({
      courseType: "ENGLISH",
      prompt: "",
      generator: "YOUTUBE_LINK",
    });
    // eslint-disable-next-line
  }, [i18n.language]);

  const handleTopicChange = (newTopic: string) => {
    setTopic(newTopic);
    onChange({ prompt: newTopic });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t("assignments.generator.properties.grade")}
            </label>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade: number) => {
                setSelectedGrade(grade);
                onChange({
                  grade,
                  courseType: "ENGLISH",
                  topicIds: [],
                });
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language as any });
            }}
          />
        </div>
      </div>
      <div>
        <h4 className={`text-sm font-medium text-gray-700 dark:text-gray-200`}>
          {t("assignments.generator.properties.cefrLevel")}
        </h4>
        <CEFRSelector
          selectedLevel={selectedCefrLevel}
          onLevelChange={(level) => {
            setSelectedCefrLevel(level);
            onChange({ cefrLevel: level });
          }}
          className="mt-1"
        />
      </div>
      <div className="space-y-4">
        <div className="animate-fadeIn">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.youtube.videoLink")}
          </label>
          <div className="flex flex-col gap-4">
            <input
              data-testid="create-assignment-from-youtube-link-input"
              type="text"
              value={youtubeLink}
              onChange={(e) => {
                setYoutubeLink(e.target.value);
                onChange({ youtubeLink: e.target.value });
              }}
              className="w-full p-3 border border-gray-300 dark:border-dark-400 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 text-sm sm:text-base"
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </div>
        </div>
        {/* All video / Specific topic radio seçenekleri ve bağlantılı alanlar */}
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4 mt-4">
          <label
            className={`flex-1 p-3 sm:p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
              !topic
                ? "border-primary-500 bg-indigo-50 dark:bg-dark-900"
                : "border-gray-200 dark:border-gray-600 hover:border-indigo-300 "
            }`}
          >
            <input
              type="radio"
              className="peer sr-only"
              name="videoScope"
              checked={!topic}
              onChange={() => {
                setTopic("");
                onChange({ prompt: "" });
              }}
            />
            <div className="space-y-1">
              <span className="font-medium block text-gray-700 dark:text-gray-200 group-hover:text-primary-600 dark:group-hover:text-primary-400 text-sm sm:text-base">
                {t("assignments.generator.youtube.allVideo")}
              </span>
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {t("assignments.generator.youtube.allVideoDesc")}
              </span>
            </div>
          </label>

          <label
            className={`flex-1 p-3 sm:p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
              topic
                ? "border-primary-500 bg-indigo-50 dark:bg-dark-900"
                : "border-gray-200 dark:border-gray-600 hover:border-indigo-300"
            }`}
          >
            <input
              type="radio"
              className="peer sr-only"
              name="videoScope"
              checked={Boolean(topic)}
              onChange={() => {
                setTopic(" ");
                onChange({ prompt: " " });
              }}
            />
            <div className="space-y-1">
              <span className="font-medium block text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 text-sm sm:text-base">
                {t("assignments.generator.youtube.specificTopic")}
              </span>
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {t("assignments.generator.youtube.specificTopicDesc")}
              </span>
            </div>
          </label>
        </div>
        {/* Eğer specific topic seçiliyse konu başlığı alanı gösterilsin */}
        {Boolean(topic) && (
          <div className="animate-fadeIn">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t("assignments.generator.properties.topic")}
            </label>
            <div className="relative">
              <textarea
                data-testid="create-assignment-from-youtube-topic-input"
                value={topic}
                onChange={(e) => {
                  setTopic(e.target.value);
                  onChange({ prompt: e.target.value });
                }}
                className="w-full h-20 sm:h-24 p-3 sm:p-4 pr-12 border border-gray-300 dark:border-dark-400 rounded-xl transition-all bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
                placeholder={t(
                  "assignments.generator.youtube.examTopicPlaceholder",
                )}
              />
              <div className="absolute right-3 bottom-3 text-xs text-gray-400">
                {topic.length}/200
              </div>
            </div>
            <Text
              variant="p"
              className="mt-2 text-sm text-gray-500 dark:text-gray-400"
            >
              {t("assignments.generator.youtube.examTopicDesc")}
            </Text>
          </div>
        )}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.youtube.videoPreview")}
          </label>
          <div className="aspect-video w-full bg-gray-100 dark:bg-dark-500 rounded-lg overflow-hidden">
            {youtubeLink ? (
              <iframe
                src={`https://www.youtube.com/embed/${
                  youtubeLink.includes("youtu.be")
                    ? youtubeLink.split("youtu.be/")[1]?.split("?")[0]
                    : youtubeLink.includes("/embed/")
                      ? youtubeLink.split("/embed/")[1]?.split("?")[0]
                      : youtubeLink.split("v=")[1]?.split("&")[0]
                }`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 p-4 text-center">
                <Text variant="p" className="text-sm sm:text-base">
                  {t("assignments.generator.youtube.videoPreview")}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubeStep;
